import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { getTeamLogoUrl } from '@/utils/teamLogo';
import type { SelectedFixtureData } from '../types';

interface FixtureOptionsPopulatorProps {
  selectedFixtureData: SelectedFixtureData | null;
  sport?: string;
  teamMap: Map<string, { name: string; abbreviation?: string }>;
}

export function FixtureOptionsPopulator({
  selectedFixtureData,
  sport,
  teamMap,
}: FixtureOptionsPopulatorProps) {
  const { values, setFieldValue } = useFormikContext<QuestionFormValues>();

  // Auto-populate options when fixture is selected
  useEffect(() => {
    if (
      selectedFixtureData &&
      values.question_type === 'team' &&
      selectedFixtureData.fixture.homeTeamId &&
      selectedFixtureData.fixture.awayTeamId &&
      sport
    ) {
      const homeTeam = teamMap.get(selectedFixtureData.fixture.homeTeamId);
      const awayTeam = teamMap.get(selectedFixtureData.fixture.awayTeamId);

      if (homeTeam && awayTeam) {
        // Generate team logo URLs
        const homeLogoUrl = homeTeam.abbreviation
          ? getTeamLogoUrl({ sport: sport.toLowerCase(), teamShortcode: homeTeam.abbreviation })
          : undefined;
        const awayLogoUrl = awayTeam.abbreviation
          ? getTeamLogoUrl({ sport: sport.toLowerCase(), teamShortcode: awayTeam.abbreviation })
          : undefined;

        // Create options array with both teams
        const fixtureData = {
          fixtureId: selectedFixtureData.fixtureId,
          homeTeamId: selectedFixtureData.fixture.homeTeamId,
          awayTeamId: selectedFixtureData.fixture.awayTeamId,
          homeTeamName: selectedFixtureData.fixture.homeTeamName || homeTeam.name,
          awayTeamName: selectedFixtureData.fixture.awayTeamName || awayTeam.name,
          scheduledDate: selectedFixtureData.fixture.scheduledDate || new Date().toISOString(),
          status: (selectedFixtureData.fixture.status || 'scheduled') as
            | 'scheduled'
            | 'live'
            | 'finished'
            | 'postponed'
            | 'cancelled',
          homeTeamAbbreviation:
            selectedFixtureData.fixture.homeTeamAbbreviation || homeTeam.abbreviation,
          awayTeamAbbreviation:
            selectedFixtureData.fixture.awayTeamAbbreviation || awayTeam.abbreviation,
        };

        const newOptions = [
          {
            option_text_en: homeTeam.name,
            option_text_fr: homeTeam.name,
            option_image_en: homeLogoUrl || '',
            option_image_fr: homeLogoUrl || '',
            third_image_en: '',
            third_image_fr: '',
            is_correct: false,
            display_order: 0,
            grading_data: {
              teamId: selectedFixtureData.fixture.homeTeamId,
              teamName: homeTeam.name,
              teamAbbr: homeTeam.abbreviation,
              fixtureData,
            },
          },
          {
            option_text_en: awayTeam.name,
            option_text_fr: awayTeam.name,
            option_image_en: awayLogoUrl || '',
            option_image_fr: awayLogoUrl || '',
            third_image_en: '',
            third_image_fr: '',
            is_correct: false,
            display_order: 1,
            grading_data: {
              teamId: selectedFixtureData.fixture.awayTeamId,
              teamName: awayTeam.name,
              teamAbbr: awayTeam.abbreviation,
              fixtureData,
            },
          },
        ];

        // Only update if options are different to avoid infinite loops
        const currentOptions = values.options || [];
        const optionsChanged =
          currentOptions.length !== 2 ||
          currentOptions[0]?.option_text_en !== homeTeam.name ||
          currentOptions[1]?.option_text_en !== awayTeam.name;

        if (optionsChanged) {
          setFieldValue('options', newOptions);
        }
      }
    }
  }, [selectedFixtureData, values.question_type, teamMap, sport, values.options, setFieldValue]);

  return null;
}
